import React, { useState, useEffect } from 'react';
import { Download, Save, CheckCircle2, FileText, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Step6Download = ({ wizardData }) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showAppAssistant, setShowAppAssistant] = useState(false);
  const [loadingAnswers, setLoadingAnswers] = useState(false);
  const [appAnswers, setAppAnswers] = useState(null);

  useEffect(() => {
    console.log('Step6Download - wizardData:', {
      hasResume: !!wizardData.tailoredResume,
      hasCoverLetter: !!wizardData.coverLetter,
      resumeLength: wizardData.tailoredResume?.length || 0,
      coverLetterLength: wizardData.coverLetter?.length || 0
    });
  }, [wizardData]);

  const copyToClipboardFallback = (text, successMessage) => {
    // Fallback method using textarea
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      document.execCommand('copy');
      toast.success(successMessage);
    } catch (err) {
      console.error('Fallback copy failed:', err);
      toast.error('Copy failed. Please use "View in New Tab" button instead.');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const handleCopy = async (text, successMessage) => {
    try {
      // Try modern clipboard API first
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch (err) {
      console.log('Clipboard API failed, using fallback method');
      // Use fallback method
      copyToClipboardFallback(text, successMessage);
    }
  };

  const formatResumeText = (text) => {
    // Convert **text** to <strong>text</strong> for bold formatting
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  };

  const handleGenerateAppAnswers = async () => {
    setLoadingAnswers(true);
    try {
      const response = await axios.post(`${API}/generate-application-answers`, {
        job_description: wizardData.jobDescription,
        tailored_resume: wizardData.tailoredResume,
        applicant_name: ''
      });
      
      setAppAnswers(response.data);
      setShowAppAssistant(true);
      toast.success('Application answers generated!');
    } catch (error) {
      console.error('Error generating answers:', error);
      toast.error('Failed to generate answers. Please try again.');
    } finally {
      setLoadingAnswers(false);
    }
  };

  const copyAnswer = (text, label) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      toast.success(`✓ ${label} copied!`);
    } catch (err) {
      toast.error('Copy failed. Please select and copy manually.');
    }
    document.body.removeChild(textarea);
  };

  const handleDownload = async (type, docType) => {
    try {
      const text = docType === 'resume' ? wizardData.tailoredResume : wizardData.coverLetter;
      const title = docType === 'resume' ? 'Tailored_Resume' : 'Cover_Letter';

      console.log('Starting download:', { 
        type, 
        docType, 
        title, 
        hasText: !!text,
        textLength: text?.length || 0,
        textPreview: text?.substring(0, 100)
      });

      if (!text || text.trim().length === 0) {
        console.error('No content available:', { docType, text });
        toast.error('No content available to download. Please complete all steps first.');
        return;
      }

      console.log('Making API request to:', `${API}/download/${type}`);

      const response = await axios({
        method: 'POST',
        url: `${API}/download/${type}`,
        data: { text, title },
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
          'Accept': type === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        }
      });

      console.log('Response received:', { 
        status: response.status, 
        statusText: response.statusText,
        contentType: response.headers['content-type'],
        dataType: typeof response.data,
        dataSize: response.data?.size,
        hasData: !!response.data
      });

      // Create blob from response
      const blob = response.data;
      
      console.log('Blob created:', {
        size: blob.size,
        type: blob.type
      });

      if (blob.size === 0) {
        throw new Error('Received empty file from server');
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title}.${type}`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      console.log('Triggering download:', { url, filename: link.download });
      
      document.body.appendChild(link);
      
      // Try to trigger download with user gesture
      link.dispatchEvent(new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      }));
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        console.log('Download cleanup complete');
      }, 100);

      toast.success(`Downloaded ${title}.${type}`);
    } catch (error) {
      console.error('Download error:', error);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        response: error.response,
        responseData: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      // Try to read error response if it's a blob
      if (error.response?.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          console.error('Error response text:', errorText);
          toast.error(`Download failed: ${errorText}`);
        } catch (e) {
          toast.error(`Failed to download file. Please try again.`);
        }
      } else {
        toast.error(`Failed to download file: ${error.response?.statusText || error.message || 'Unknown error'}`);
      }
    }
  };

  const handleSaveSession = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/sessions`, {
        job_description: wizardData.jobDescription,
        original_resume_text: wizardData.resumeText,
        tailored_resume_text: wizardData.tailoredResume,
        cover_letter_text: wizardData.coverLetter,
        analysis: wizardData.analysis || {},
        tools_to_validate: wizardData.toolsToValidate || []
      });

      setSaved(true);
      toast.success('Session saved successfully!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save session');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="text-center">
        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
          <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-3 sm:mb-4">
          All Done!
        </h2>
        <p className="text-base sm:text-lg text-muted-foreground px-4">
          Your tailored resume and cover letter are ready
        </p>
        
        {/* Debug Info */}
        {(!wizardData.tailoredResume || !wizardData.coverLetter) && (
          <div className="mt-4 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-sm">
            <p className="text-orange-800 dark:text-orange-200">
              ⚠️ Debug Info: 
              {!wizardData.tailoredResume && ' Resume not found. '}
              {!wizardData.coverLetter && ' Cover letter not found. '}
              Please complete all previous steps.
            </p>
          </div>
        )}
      </div>

      {/* Application Assistant Feature */}
      <Card className="p-4 sm:p-6 bg-blue-50 dark:bg-blue-950/20 border-2 border-blue-500">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-2">🚀 Application Assistant</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Get AI-generated answers to common application questions. Copy and paste them when applying!
              </p>
              
              {!showAppAssistant ? (
                <Button
                  onClick={handleGenerateAppAnswers}
                  disabled={loadingAnswers}
                  className="w-full sm:w-auto"
                >
                  {loadingAnswers ? (
                    <>
                      <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 w-4 h-4" />
                      Generate Application Answers
                    </>
                  )}
                </Button>
              ) : (
                <div className="space-y-4">
                  {appAnswers && (
                    <>
                      {/* Why Interested */}
                      <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border">
                        <div className="flex justify-between items-start mb-2">
                          <p className="font-semibold text-sm">Why are you interested in this role?</p>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyAnswer(appAnswers.why_interested, 'Answer')}
                          >
                            📋 Copy
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">{appAnswers.why_interested}</p>
                      </div>

                      {/* Why Company */}
                      <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border">
                        <div className="flex justify-between items-start mb-2">
                          <p className="font-semibold text-sm">Why do you want to work for this company?</p>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyAnswer(appAnswers.why_company, 'Answer')}
                          >
                            📋 Copy
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">{appAnswers.why_company}</p>
                      </div>

                      {/* Good Fit */}
                      <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border">
                        <div className="flex justify-between items-start mb-2">
                          <p className="font-semibold text-sm">What makes you a good fit?</p>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyAnswer(appAnswers.good_fit, 'Answer')}
                          >
                            📋 Copy
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">{appAnswers.good_fit}</p>
                      </div>

                      {/* About Yourself */}
                      <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border">
                        <div className="flex justify-between items-start mb-2">
                          <p className="font-semibold text-sm">Tell us about yourself</p>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyAnswer(appAnswers.about_yourself, 'Answer')}
                          >
                            📋 Copy
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">{appAnswers.about_yourself}</p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Resume Display */}

      {/* Download Options */}
      {/* SIMPLEST METHOD - Show on Page */}
      <Card className="p-4 sm:p-6 bg-emerald-50 dark:bg-emerald-950/20 border-2 border-emerald-600 mb-4 sm:mb-6">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xl">✓</span>
            </div>
            <div className="flex-1">
              <h3 className="text-base sm:text-lg font-bold mb-2 text-emerald-900 dark:text-emerald-100">Your Tailored Resume (Ready to Copy)</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3">
                Select all the text below, copy it (Ctrl+A, then Ctrl+C), and paste into Microsoft Word or Google Docs. Then save as DOCX or PDF.
              </p>
            </div>
          </div>
          
          {/* Resume text display with copy button */}
          <div className="relative">
            <Button
              size="sm"
              onClick={() => {
                const textarea = document.createElement('textarea');
                textarea.value = wizardData.tailoredResume;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                try {
                  document.execCommand('copy');
                  toast.success('✓ Resume copied! Paste into Word or Google Docs');
                } catch (err) {
                  toast.error('Please manually select and copy the text below');
                }
                document.body.removeChild(textarea);
              }}
              className="absolute top-2 right-2 z-10"
            >
              📋 Copy All
            </Button>
            <ScrollArea className="h-[400px] w-full border-2 border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-900">
              <div 
                className="text-sm whitespace-pre-wrap font-sans select-all"
                dangerouslySetInnerHTML={{
                  __html: formatResumeText(wizardData.tailoredResume)
                }}
              />
            </ScrollArea>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                const printWindow = window.open('', '_blank');
                const formattedText = formatResumeText(wizardData.tailoredResume);
                printWindow.document.write(`
                  <!DOCTYPE html>
                  <html>
                  <head>
                    <title>Tailored Resume</title>
                    <style>
                      body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; line-height: 1.6; }
                      strong { font-weight: bold; }
                      @media print { body { padding: 20px; } }
                    </style>
                  </head>
                  <body>
                    <div style="white-space: pre-wrap; font-family: Arial;">${formattedText}</div>
                  </body>
                  </html>
                `);
                printWindow.document.close();
                setTimeout(() => printWindow.print(), 250);
              }}
              className="flex-1"
            >
              🖨️ Print to PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const win = window.open('', '_blank');
                const formattedText = formatResumeText(wizardData.tailoredResume);
                win.document.write(`
                  <!DOCTYPE html>
                  <html>
                  <head>
                    <title>Tailored Resume</title>
                    <style>
                      body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; line-height: 1.6; }
                      strong { font-weight: bold; }
                    </style>
                  </head>
                  <body>
                    <div style="white-space: pre-wrap;">${formattedText}</div>
                  </body>
                  </html>
                `);
              }}
              className="flex-1"
            >
              👁️ View in New Tab
            </Button>
          </div>
        </div>
      </Card>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold mb-4">Optional: Try Download Files</h3>
        <p className="text-sm text-muted-foreground mb-4">If your browser allows downloads, you can try these options. Otherwise, use the copy method above.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-primary" />
                <div>
                  <p className="font-semibold">DOCX Format</p>
                  <p className="text-sm text-muted-foreground">Editable document</p>
                </div>
              </div>
            </div>
            <Button
              data-testid="download-resume-docx-btn"
              onClick={() => handleDownload('docx', 'resume')}
              className="w-full"
              variant="outline"
            >
              <Download className="mr-2 w-4 h-4" />
              Download DOCX
            </Button>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-primary" />
                <div>
                  <p className="font-semibold">PDF Format</p>
                  <p className="text-sm text-muted-foreground">Print-ready</p>
                </div>
              </div>
            </div>
            <Button
              data-testid="download-resume-pdf-btn"
              onClick={() => handleDownload('pdf', 'resume')}
              className="w-full"
              variant="outline"
            >
              <Download className="mr-2 w-4 h-4" />
              Download PDF
            </Button>
          </Card>
        </div>
      </div>

      {/* SIMPLEST METHOD - Show Cover Letter on Page */}
      <Card className="p-4 sm:p-6 bg-emerald-50 dark:bg-emerald-950/20 border-2 border-emerald-600 mb-4 sm:mb-6">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xl">✓</span>
            </div>
            <div className="flex-1">
              <h3 className="text-base sm:text-lg font-bold mb-2 text-emerald-900 dark:text-emerald-100">Your Cover Letter (Ready to Copy)</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3">
                Select all the text below, copy it, and paste into Microsoft Word or Google Docs. Then save as DOCX or PDF.
              </p>
            </div>
          </div>
          
          {/* Cover letter text display with copy button */}
          <div className="relative">
            <Button
              size="sm"
              onClick={() => {
                const textarea = document.createElement('textarea');
                textarea.value = wizardData.coverLetter;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                try {
                  document.execCommand('copy');
                  toast.success('✓ Cover letter copied! Paste into Word or Google Docs');
                } catch (err) {
                  toast.error('Please manually select and copy the text below');
                }
                document.body.removeChild(textarea);
              }}
              className="absolute top-2 right-2 z-10"
            >
              📋 Copy All
            </Button>
            <ScrollArea className="h-[400px] w-full border-2 border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-900">
              <div 
                className="text-sm whitespace-pre-wrap font-sans select-all"
                dangerouslySetInnerHTML={{
                  __html: formatResumeText(wizardData.coverLetter)
                }}
              />
            </ScrollArea>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                const printWindow = window.open('', '_blank');
                const formattedText = formatResumeText(wizardData.coverLetter);
                printWindow.document.write(`
                  <!DOCTYPE html>
                  <html>
                  <head>
                    <title>Cover Letter</title>
                    <style>
                      body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; line-height: 1.6; }
                      strong { font-weight: bold; }
                      @media print { body { padding: 20px; } }
                    </style>
                  </head>
                  <body>
                    <div style="white-space: pre-wrap; font-family: Arial;">${formattedText}</div>
                  </body>
                  </html>
                `);
                printWindow.document.close();
                setTimeout(() => printWindow.print(), 250);
              }}
              className="flex-1"
            >
              🖨️ Print to PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const win = window.open('', '_blank');
                const formattedText = formatResumeText(wizardData.coverLetter);
                win.document.write(`
                  <!DOCTYPE html>
                  <html>
                  <head>
                    <title>Cover Letter</title>
                    <style>
                      body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; line-height: 1.6; }
                      strong { font-weight: bold; }
                    </style>
                  </head>
                  <body>
                    <div style="white-space: pre-wrap;">${formattedText}</div>
                  </body>
                  </html>
                `);
              }}
              className="flex-1"
            >
              👁️ View in New Tab
            </Button>
          </div>
        </div>
      </Card>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold mb-4">Optional: Try Download Cover Letter</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-primary" />
                <div>
                  <p className="font-semibold">DOCX Format</p>
                  <p className="text-sm text-muted-foreground">Editable document</p>
                </div>
              </div>
            </div>
            <Button
              data-testid="download-cover-letter-docx-btn"
              onClick={() => handleDownload('docx', 'cover-letter')}
              className="w-full"
              variant="outline"
            >
              <Download className="mr-2 w-4 h-4" />
              Download DOCX
            </Button>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-primary" />
                <div>
                  <p className="font-semibold">PDF Format</p>
                  <p className="text-sm text-muted-foreground">Print-ready</p>
                </div>
              </div>
            </div>
            <Button
              data-testid="download-cover-letter-pdf-btn"
              onClick={() => handleDownload('pdf', 'cover-letter')}
              className="w-full"
              variant="outline"
            >
              <Download className="mr-2 w-4 h-4" />
              Download PDF
            </Button>
          </Card>
        </div>
      </div>

      {/* Save Session */}
      <Card className="p-6 bg-slate-50 dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold mb-1">Save This Session</p>
            <p className="text-sm text-muted-foreground">Access this tailored resume later</p>
          </div>
          <Button
            data-testid="save-session-btn"
            onClick={handleSaveSession}
            disabled={saving || saved}
            variant={saved ? 'outline' : 'default'}
          >
            {saved ? (
              <>
                <CheckCircle2 className="mr-2 w-4 h-4" />
                Saved
              </>
            ) : (
              <>
                <Save className="mr-2 w-4 h-4" />
                {saving ? 'Saving...' : 'Save Session'}
              </>
            )}
          </Button>
        </div>
      </Card>

      <div className="flex justify-center pt-4">
        <Button
          data-testid="back-to-home-final-btn"
          size="lg"
          variant="outline"
          onClick={() => navigate('/')}
          className="h-12 px-8 font-semibold"
        >
          <Home className="mr-2 w-5 h-5" />
          Back to Home
        </Button>
      </div>
    </div>
  );
};

export default Step6Download;
